import { FC, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { IconButton, InputAdornment } from '@mui/material';
import {
	Lock as LockIcon,
	VisibilityOff as VisibilityOffIcon,
	Visibility as VisibilityIcon
} from '@mui/icons-material';

import { generateValidation } from 'shared/constants/validation';
import { API_CONFIG } from 'shared/constants/api';
import AMBGTextField from 'shared/components/AMBGTextField/AMBGTextField';
import AMBGButton from 'shared/components/AMBGButton/AMBGButton';
import { ROUTES } from 'shared/constants/constant';
import HttpService from 'shared/services/Http.service';

import { StyledAuthTitle } from 'assets/styles/Common.style';

interface IResetPasswordFormInput {
	password: string;
}

const ResetPasswordScreen: FC = () => {
	const { token } = useParams();
	const navigate = useNavigate();
	const [isLoading, setLoading] = useState<boolean>(false);
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [resetToken, setResetToken] = useState<string>('');

	useEffect(() => {
		if (token) {
			setResetToken(token);
		} else {
			navigate(ROUTES.LOGIN);
		}
	}, [navigate, token]);

	const {
		handleSubmit,
		control,
		formState: { errors }
	} = useForm<IResetPasswordFormInput>({
		defaultValues: { password: '' }
	});

	const submitResetPasswordForm = useCallback(
		async (values: IResetPasswordFormInput) => {
			try {
				setLoading(true);
				const params = { password: values.password, token: resetToken };
				await HttpService.post(API_CONFIG.resetPassword, params);
				setLoading(false);
				setResetToken('');
				navigate(ROUTES.LOGIN);
			} catch (error) {
				setLoading(false);
			}
		},
		[navigate, resetToken]
	);

	return (
		<>
			<StyledAuthTitle data-testid='resetPasswordTitle'>Reset Password</StyledAuthTitle>
			<form className='width--full' onSubmit={handleSubmit(submitResetPasswordForm)}>
				<Controller
					control={control}
					rules={generateValidation('Password').password}
					name='password'
					render={({ field: { ref, ...rest } }) => (
						<AMBGTextField
							label='New Password'
							type={showPassword ? 'text' : 'password'}
							data-testid='password'
							placeholder='Enter Your New Password'
							boxProps={{ marginBottom: '36px' }}
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<LockIcon fontSize='small' />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position='end'>
										<IconButton
											aria-label='toggle password visibility'
											data-testid='passwordToggle'
											onClick={() => setShowPassword(!showPassword)}
											edge='end'
										>
											{showPassword ? (
												<VisibilityOffIcon fontSize='small' />
											) : (
												<VisibilityIcon fontSize='small' />
											)}
										</IconButton>
									</InputAdornment>
								)
							}}
							error={!!errors.password}
							helperText={errors.password?.message}
							inputRef={ref}
							{...rest}
						/>
					)}
				/>
				<AMBGButton data-testid='submitButton' fullWidth text='Submit' loading={isLoading} type='submit' />
			</form>
		</>
	);
};

export default ResetPasswordScreen;
